import { InfinityIcon } from "@images/svgIcons/infinityIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleFormPageProps } from "@modules/programBundle/components/programBundleForm"
import { usePrograms } from "@modules/programs/hooks/usePrograms"
import { ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOffStatus, ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import { ItemProgram } from "@modules/programs/programTypes"
import { getDisplayStatus } from "@modules/programs/programUtils"
import { HUButton } from "@modules/ui/components/huButton"
import { HUCheckbox } from "@modules/ui/components/huCheckbox"
import { HUText } from "@modules/ui/components/huText"
import Line from "@modules/ui/components/line"
import { ArrowLeft, ArrowRight } from "lucide-react"
import { Tooltip } from "primereact/tooltip"
import { useTheme } from "styled-components"

export const ProgramBundleFormSecondPage = ({
  formik,
  getFormErrorMessage,
  handleSubmit,
  setPageNumber,
}: ProgramBundleFormPageProps) => {
  const theme = useTheme()
  const language = useLanguage()
  const LENGTH_SHORT_TITLE = 32
  const userProgramsAsync = usePrograms({ programFilter: "OWNED" })
  const typeOfFilter = formik.values.type === ProgramBundleType.VIRTUAL ? ProgramInfiniteTypeOf : ProgramOnOffTypeOf
  const filterPrograms = userProgramsAsync.result.filter((program) => program._programType === typeOfFilter)

  const onCheckProgram = (program: ItemProgram) => {
    if ("status" in program && program.status === ProgramOnOffStatus.DRAFT) return

    if (formik.values.programIds.some((selectedProgram) => selectedProgram === program.id))
      formik.setFieldValue(
        "programIds",
        formik.values.programIds.filter((selectedProgram) => selectedProgram !== program.id),
      )
    else formik.setFieldValue("programIds", [...formik.values.programIds, program.id])
  }

  return (
    <div className="flex flex-column">
      {/* TITLE */}
      <div className="flex flex-column">
        <HUText fontStyle="BS" color={theme.neutralColor600}>
          {word("programBundle.form.secondPage.step")}
        </HUText>
        <HUText fontStyle="HM" color={theme.neutralColor900} className="mt-4">
          {word("programBundle.form.secondPage.title")}
        </HUText>
        <HUText fontStyle="BM" color={theme.neutralColor900} className="mt-1">
          {word("programBundle.form.secondPage.subtitle")}
        </HUText>
      </div>

      {/* PROGRAM TYPE */}
      <div className="flex flex-column mt-4">
        <div className="flex justify-content-between">
          <div className="flex align-items-center gap-1">
            {formik.values.type === ProgramBundleType.VIRTUAL ? (
              <>
                <InfinityIcon color={theme.neutralColor600} width={15} height={15} />
                <HUText fontStyle="LM" color={theme.neutralColor600}>
                  {word("programBundle.typeOfPrograms.infitePrograms")}
                </HUText>
              </>
            ) : (
              <>
                <OnOffIcon color={theme.neutralColor600} width={15} height={15} />
                <HUText fontStyle="LM" color={theme.neutralColor600}>
                  {word("programBundle.typeOfPrograms.onOffPrograms")}
                </HUText>
              </>
            )}
          </div>
          <div className="flex gap-1">
            <HUText fontStyle="BS" color={theme.neutralColor700}>
              {formik.values.programIds.length} / {filterPrograms.length}
            </HUText>
            <HUText fontStyle="BS" color={theme.neutralColor700}>
              {word("global.selected", { count: formik.values.programIds.length })}
            </HUText>
          </div>
        </div>
        <Line color={theme.neutralColor300} strokeWidth={1} className="m-0 mt-2" />
      </div>

      {/* PROGRAM LIST */}
      {filterPrograms.map((program) => {
        const isDraft = "status" in program && program.status === ProgramOnOffStatus.DRAFT
        return (
          <div className="flex flex-column" key={program.id}>
            <div
              className={"flex gap-3 mt-2 " + (!isDraft && "cursor-pointer")}
              onClick={() => onCheckProgram(program)}
            >
              <HUCheckbox
                inputKey={program.id}
                name="option"
                value={program.id}
                checked={formik.values.programIds.some((selectedProgram) => selectedProgram === program.id)}
                onChange={() => {}}
                color={theme.primaryColor}
                checkIconColor={theme.neutralColor700}
                disabled={isDraft}
              />
              <div className="flex flex-column gap-1 align-items-start">
                {program.title.length > LENGTH_SHORT_TITLE && (
                  <Tooltip className="tooltip-container" target={`.tooltip${program.id}`}>
                    <div className="tooltip-content">
                      <HUText type="label" fontStyle="BM" style={{ textAlign: "left" }}>
                        {program.title}
                      </HUText>
                    </div>
                  </Tooltip>
                )}
                <div className={`tooltip${program.id} flex`} data-pr-showdelay={200} data-pr-position="bottom">
                  <HUText fontStyle="TS" color={theme.neutralColor900}>
                    {program.title.length > LENGTH_SHORT_TITLE
                      ? program.title.substring(0, LENGTH_SHORT_TITLE) + "..."
                      : program.title}
                  </HUText>
                </div>

                {isDraft ? (
                  <div className="align-items-center bg-neutral-100 px-2 rounded-33">
                    <HUText fontStyle="BS">{getDisplayStatus(program.status)}</HUText>
                  </div>
                ) : program.price?.currency && program.price.value ? (
                  <HUText fontStyle="BS" color={theme.neutralColor700}>
                    {formik.values.type === ProgramBundleType.VIRTUAL
                      ? `${formatCurrency(program.price.value, program.price.currency, language)} / ${word("global.month.label", { count: 2 })}`
                      : formatCurrency(program.price.value, program.price.currency, language)}
                  </HUText>
                ) : (
                  <HUText fontStyle="BS" color={theme.neutralColor700}>
                    {word("program.price.free")}
                  </HUText>
                )}
              </div>
            </div>
            <Line color={theme.neutralColor300} strokeWidth={1} className="m-0 mt-2" />
          </div>
        )
      })}

      {/* ERROR MESSAGE */}
      {getFormErrorMessage("programIds") && (
        <HUText fontStyle="BM" color={theme.errorColor} className="mt-2">
          {getFormErrorMessage("programIds")}
        </HUText>
      )}

      {/* NEXT BUTTON */}
      <div className="flex gap-5 mt-5 mb-6">
        <HUButton
          text={word("global.previous")}
          onClick={() => setPageNumber((prev) => prev - 1)}
          type="Rounded"
          size="M"
          colorType="Quaternary"
          fullWidth
          icon={{
            iconView: <ArrowLeft size={18} color={theme.mainBlack} />,
          }}
        />
        <HUButton
          text={word("global.next")}
          onClick={handleSubmit}
          type="Right_Icon"
          size="M"
          colorType="Primary"
          fullWidth
          icon={{
            iconView: (
              <ArrowRight
                size={18}
                color={formik.values.programIds.length === 0 ? theme.neutralColor300 : theme.mainBlack}
              />
            ),
          }}
          disabled={formik.values.programIds.length === 0}
        />
      </div>
    </div>
  )
}
